import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ExpenseService } from '@/services/expense.service';
import {
  Expense,
  ExpenseReport,
  ExpenseStatus,
  ExpenseCategoryLabels,
} from '@/models/expense.model';
import { format } from 'date-fns';

type FormState = Partial<ExpenseReport>;

export const ExpenseReportForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const [formData, setFormData] = useState<FormState>({
    title: '',
    description: '',
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    currency: 'USD',
    expenseIds: [],
    tags: [],
  });
  const [selectedExpenses, setSelectedExpenses] = useState<Expense[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch existing report if editing
  const { data: report, isLoading: reportLoading } = useQuery({
    queryKey: ['expenseReport', id],
    queryFn: () => (id ? ExpenseService.getReportById(id) : null),
    enabled: isEditing && Boolean(id),
  });

  // Fetch available expenses (not in other reports, drafts or rejected for the current user)
  const { data: availableExpenses = [], isLoading: expensesLoading } = useQuery({
    queryKey: ['availableExpenses'],
    queryFn: async () => {
      const expenses = await ExpenseService.getAllExpenses();
      // Filter to show only expenses that can be added to a report
      return expenses.filter(e => 
        !e.reportId && 
        (e.status === ExpenseStatus.draft || e.status === ExpenseStatus.rejected)
      );
    },
  });

  useEffect(() => {
    if (report) {
      setFormData(report);
      if (report.expenses) {
        setSelectedExpenses(report.expenses);
      }
    }
  }, [report]);

  const addExpense = (expense: Expense) => {
    if (!selectedExpenses.find(e => e.id === expense.id)) {
      setSelectedExpenses([...selectedExpenses, expense]);
    }
  };

  const removeExpense = (expenseId: string) => {
    setSelectedExpenses(selectedExpenses.filter(e => e.id !== expenseId));
  };

  const addTag = () => {
    const next = tagInput.trim();
    if (!next || formData.tags?.includes(next)) {
      setTagInput('');
      return;
    }
    setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), next] }));
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags?.filter(t => t !== tag) || [] }));
  };

  const calculateTotals = () => {
    const subtotal = selectedExpenses.reduce((sum, e) => sum + e.amount, 0);
    const taxTotal = selectedExpenses.reduce((sum, e) => sum + (e.taxAmount || 0), 0);
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  };

  const mutation = useMutation({
    mutationFn: async (data: FormState) => {
      if (!data.title?.trim()) throw new Error('Report title is required');
      if (selectedExpenses.length === 0) throw new Error('At least one expense is required');

      const totals = calculateTotals();
      
      // Add employee info
      data.employeeId = profile?.id || '';
      data.employeeName = profile?.fullName || profile?.email || '';
      data.employeeEmail = profile?.email || '';
      data.expenseIds = selectedExpenses.map(e => e.id);
      data.subtotal = totals.subtotal;
      data.taxTotal = totals.taxTotal;
      data.total = totals.total;

      if (isEditing && id) {
        await ExpenseService.updateReport(id, data);
        return id;
      }
      return await ExpenseService.createReport(data);
    },
    onSuccess: (reportId) => {
      queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
      queryClient.invalidateQueries({ queryKey: ['expenseReport', reportId] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      navigate(`/expenses/reports/${reportId}`);
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save report');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      await mutation.mutateAsync(formData);
    } catch (err) {
      console.error('[ExpenseReportForm] Submit error:', err);
    }
  };

  const handleSaveAsDraft = async () => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ ...formData, status: ExpenseStatus.draft });
    } catch (err) {
      console.error('[ExpenseReportForm] Save draft error:', err);
    }
  };

  const handleSubmitForApproval = async () => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ ...formData, status: ExpenseStatus.submitted, submittedAt: new Date() });
    } catch (err) {
      console.error('[ExpenseReportForm] Submit for approval error:', err);
    }
  };

  const totals = calculateTotals();

  if (reportLoading || expensesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link to="/expenses" className="text-blue-600 hover:text-blue-700 text-sm">
          ← Back to Expenses
        </Link>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Expense Report' : 'Create Expense Report'}
      </h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {/* Report Details */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Report Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Q1 2024 Travel Expenses"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => setFormData({ ...formData, endDate: new Date(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Optional description for this expense report..."
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Selected Expenses */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Selected Expenses ({selectedExpenses.length})
              </h2>
              
              {selectedExpenses.length === 0 ? (
                <p className="text-sm text-gray-500">No expenses selected. Add expenses from the list below.</p>
              ) : (
                <div className="space-y-3">
                  {selectedExpenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-md"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                        <p className="text-xs text-gray-500">
                          {ExpenseCategoryLabels[expense.category]} • {format(expense.expenseDate, 'MMM d, yyyy')}
                          {expense.merchant && ` • ${expense.merchant}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-gray-900">
                          ${expense.amount.toFixed(2)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeExpense(expense.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Expenses */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Available Expenses ({availableExpenses.filter(e => !selectedExpenses.find(s => s.id === e.id)).length})
              </h2>
              
              {availableExpenses.length === 0 ? (
                <p className="text-sm text-gray-500">No expenses available to add.</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {availableExpenses
                    .filter(e => !selectedExpenses.find(s => s.id === e.id))
                    .map((expense) => (
                      <div
                        key={expense.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                          <p className="text-xs text-gray-500">
                            {ExpenseCategoryLabels[expense.category]} • {format(expense.expenseDate, 'MMM d, yyyy')}
                            {expense.merchant && ` • ${expense.merchant}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-medium text-gray-900">
                            ${expense.amount.toFixed(2)}
                          </span>
                          <button
                            type="button"
                            onClick={() => addExpense(expense)}
                            className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-300 rounded hover:bg-blue-50"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tags</h2>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Add a tag and press Enter"
                  className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Add
                </button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-1 text-blue-600 hover:text-blue-800">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
              
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Expenses</dt>
                  <dd className="text-sm font-medium text-gray-900">{selectedExpenses.length}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Subtotal</dt>
                  <dd className="text-sm font-medium text-gray-900">${totals.subtotal.toFixed(2)}</dd>
                </div>
                {totals.taxTotal > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Tax</dt>
                    <dd className="text-sm font-medium text-gray-900">${totals.taxTotal.toFixed(2)}</dd>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t">
                  <dt className="text-base font-medium text-gray-900">Total</dt>
                  <dd className="text-base font-bold text-gray-900">${totals.total.toFixed(2)}</dd>
                </div>
              </dl>

              {/* Category Breakdown */}
              {selectedExpenses.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">By Category</h3>
                  <div className="space-y-2">
                    {Object.entries(
                      selectedExpenses.reduce((acc, e) => {
                        acc[e.category] = (acc[e.category] || 0) + e.amount;
                        return acc;
                      }, {} as Record<string, number>)
                    )
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => (
                        <div key={category} className="flex justify-between text-sm">
                          <span className="text-gray-600">{ExpenseCategoryLabels[category as keyof typeof ExpenseCategoryLabels] || category}</span>
                          <span className="font-medium">${amount.toFixed(2)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 pt-6 border-t space-y-3">
                <button
                  type="button"
                  onClick={handleSaveAsDraft}
                  disabled={mutation.isPending || selectedExpenses.length === 0}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {mutation.isPending ? 'Saving...' : 'Save as Draft'}
                </button>
                <button
                  type="button"
                  onClick={handleSubmitForApproval}
                  disabled={mutation.isPending || selectedExpenses.length === 0}
                  className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {mutation.isPending ? 'Submitting...' : 'Submit for Approval'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/expenses')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
